import { fetchJsonWithAuth, apiGetWithAuth } from './authApi';
import { AUTH_URL, CANDIDATES_URL, JOBS_URL, SUBMISSIONS_URL } from './apiConfig';

function getTenantContext() {
  return {
    tenantId: localStorage.getItem('tenantId') || '',
    businessId: localStorage.getItem('businessId') || '',
    businessUnitId: localStorage.getItem('businessUnitId') || '',
  };
}

function getUserId() {
  return Number(localStorage.getItem('userId')) || 0;
}

const unwrap = (json) => json?.data ?? json;

// ── Change Status ────────────────────────────────────────────────────────────

export async function changeModuleStatus(module, ids, status) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/module-data-update', {
    method: 'PUT',
    body: JSON.stringify({ module, ids, updates: { status } }),
  });
  return unwrap(json);
}

// ── Delete ───────────────────────────────────────────────────────────────────

export async function deleteJobRecords(jobIds) {
  return fetchJsonWithAuth(JOBS_URL, '/delete', {
    method: 'POST',
    body: JSON.stringify({
      schemaVersion: 1,
      ...getTenantContext(),
      jobId: jobIds,
    }),
  });
}

export async function deleteCandidateRecords(ids) {
  const results = await Promise.all(
    ids.map((id) =>
      fetchJsonWithAuth(CANDIDATES_URL, `/delete?_id=${encodeURIComponent(id)}`, { method: 'PUT' }),
    ),
  );
  return results[results.length - 1] ?? {};
}

export async function deleteGenericRecords(module, ids) {
  return fetchJsonWithAuth(AUTH_URL, '/module-data-delete', {
    method: 'POST',
    body: JSON.stringify({ module, ids }),
  });
}

export function deleteModuleRecords(module, ids) {
  if (module === 'job') return deleteJobRecords(ids);
  if (module === 'candidate' || module === 'candidates') return deleteCandidateRecords(ids);
  return deleteGenericRecords(module, ids);
}

// ── Job search (shared by quick-submit and tag-to-job) ───────────────────────

export async function searchJobs(searchQuery) {
  const json = await fetchJsonWithAuth(JOBS_URL, '/joblist', {
    method: 'POST',
    body: JSON.stringify({
      ...getTenantContext(),
      searchQuery,
      userId: getUserId(),
    }),
  });
  return unwrap(json);
}

// ── Quick Submit ─────────────────────────────────────────────────────────────

export async function searchCandidatesForSubmit(jobId, search = '') {
  const json = await fetchJsonWithAuth(CANDIDATES_URL, '/quicksubmit/list', {
    method: 'POST',
    body: JSON.stringify({ jobId, search }),
  });
  return unwrap(json);
}

export async function quickSubmitCandidate({ jobId, candidateId }) {
  const json = await fetchJsonWithAuth(SUBMISSIONS_URL, '/review', {
    method: 'POST',
    body: JSON.stringify({
      ...getTenantContext(),
      jobId,
      candidateId: Array.isArray(candidateId) ? candidateId : [candidateId],
    }),
  });
  return unwrap(json);
}

// ── Add Notes & Share ─────────────────────────────────────────────────────────

export async function getMentionUsers(search = '') {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiGetWithAuth(JOBS_URL, `/users/any${params}`);
}

export async function addStickyNote({ relatedIds, module, title, targetDate, notes, mentionedUsers = [] }) {
  return fetchJsonWithAuth(JOBS_URL, '/notes', {
    method: 'POST',
    body: JSON.stringify({
      schemaVersion: 1,
      ...getTenantContext(),
      title,
      notes,
      notesFor: module || 'general',
      // The backend expects an ObjectID or null here, not an empty array —
      // a plain dashboard note (no selected row) has nothing to relate to.
      notesRelatedId: Array.isArray(relatedIds) && relatedIds.length ? relatedIds : null,
      receiver: mentionedUsers,
      // An empty-string reminder time fails backend time parsing — omit the
      // whole object (like a null target date) instead of sending "".
      reminder: targetDate ? { type: 'single', time: targetDate, interval: '' } : null,
      targetDate: targetDate || null,
    }),
  });
}

// ── Tag to Job ────────────────────────────────────────────────────────────────

export async function tagCandidatesToJob({ jobId, candidateIds, hiringStage }) {
  return fetchJsonWithAuth(JOBS_URL, '/source/hiringStgUpdate', {
    method: 'PUT',
    body: JSON.stringify({
      ...getTenantContext(),
      multiJobId: Array.isArray(jobId) ? jobId : [jobId],
      applicantId: Array.isArray(candidateIds) ? candidateIds : [candidateIds],
      updateStatus: String(hiringStage ?? '').toLowerCase(),
      updateStatusType: 'call',
      responseType: 'responded',
      hiriringStatusChangeType: 'tagToJobs',
      comments: '',
      userId: getUserId(),
    }),
  });
}

// ── Add to Groups ─────────────────────────────────────────────────────────────

export async function getCandidateGroups() {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    '/masters/dropdown?masterName=candidateGroups&offset=0&limit=50',
  );
  return unwrap(json);
}

export async function addCandidatesToGroup({ candidateIds, groupId, groupName }) {
  const json = await fetchJsonWithAuth(CANDIDATES_URL, '/actions/update-group', {
    method: 'PUT',
    body: JSON.stringify({ candidateIds, groupId, groupName }),
  });
  return unwrap(json);
}

// ── Self Assign ───────────────────────────────────────────────────────────────

export async function selfAssignJob({ jobIds, rate, priority }) {
  return fetchJsonWithAuth(JOBS_URL, '/assign?type=self', {
    method: 'PUT',
    body: JSON.stringify({
      multiJobId: Array.isArray(jobIds) ? jobIds : [jobIds],
      userId: getUserId(),
      schemaVersion: 1,
      ...getTenantContext(),
      jobPayDetails: rate ?? {},
      priority: priority ?? false,
      priorityFrom: null,
      recruitersData: [],
    }),
  });
}

// ── Others Assign ─────────────────────────────────────────────────────────────

export async function getAssignableUsers() {
  const data = await apiGetWithAuth(JOBS_URL, '/users/manager');
  const users = Array.isArray(data) ? data : (data ? [data] : []);
  return users.map((user) => ({
    ...user,
    label: user.name ?? user.userName ?? user.email ?? String(user.userId ?? ''),
    value: String(user.userId ?? user.id ?? user._id ?? ''),
  }));
}

export async function getJobAssignedRecruiters(jobId) {
  if (!jobId) return null;
  return apiGetWithAuth(JOBS_URL, `/self-assign?jobId=${encodeURIComponent(jobId)}`);
}

export async function assignJobToOthers({ jobIds, userId, rate, priority, removedIds, existingRecruiters }) {
  const keptRecruiters = (existingRecruiters ?? []).filter((r) => {
    const id = String(r?.user_id ?? r?.id ?? r?.userId ?? '');
    return id && !(removedIds ?? []).includes(id);
  });

  return fetchJsonWithAuth(JOBS_URL, '/assign?type=other', {
    method: 'PUT',
    body: JSON.stringify({
      multiJobId: Array.isArray(jobIds) ? jobIds : [jobIds],
      userId,
      schemaVersion: 1,
      ...getTenantContext(),
      jobPayDetails: rate ?? {},
      priority: priority ?? false,
      priorityFrom: null,
      recruitersData: keptRecruiters,
    }),
  });
}

// ── Move to Onboarding ────────────────────────────────────────────────────────

export async function moveToOnboarding({ submissionId, isMail = false }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/move-to-onboarding', {
    method: 'POST',
    body: JSON.stringify({ submissionId, isMail: !!isMail }),
  });
  return unwrap(json);
}

// ── Approve Rate ──────────────────────────────────────────────────────────────

export async function approveSubmission(submissionId, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/approve', {
    method: 'POST',
    body: JSON.stringify({ submissionId, ...payload }),
  });
  return unwrap(json);
}

export async function getVMSApprove(submissionId) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/submissions/vms-approve?submissionId=${encodeURIComponent(submissionId)}`,
  );
  return unwrap(json);
}

// ── Reject ────────────────────────────────────────────────────────────────────

export async function rejectSubmission({ submissionId, rejectReasonId, rejectReason, rejectReasonComments }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/reject', {
    method: 'POST',
    body: JSON.stringify({ submissionId, rejectReasonId, rejectReason, rejectReasonComments }),
  });
  return unwrap(json);
}

// ── Remove from Shortlist ─────────────────────────────────────────────────────

export async function removeFromShortlist({ jobId, candidateIds }) {
  return fetchJsonWithAuth(JOBS_URL, '/source/hiringStgUpdate', {
    method: 'PUT',
    body: JSON.stringify({
      ...getTenantContext(),
      multiJobId: Array.isArray(jobId) ? jobId : [jobId],
      applicantId: Array.isArray(candidateIds) ? candidateIds : [candidateIds],
      updateStatus: 'pipeline',
      updateStatusType: 'call',
      responseType: 'responded',
      hiriringStatusChangeType: 'tagToJobs',
      comments: '',
      userId: getUserId(),
    }),
  });
}
